import React, { useCallback, useMemo } from 'react';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import EntityTableHeader, { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import {
  RetailSalesResponseAdditionalValues,
  TrafficResponseAdditionalValues,
  TrafficDatum
} from './serverProxy/types';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import moment from 'moment-timezone';
import { useKeyMetricForecastSummaryData } from './serverProxy';
import { useBeaconSalesDataByWeekId } from './serverProxy/useBeaconSalesDataByWeekId';
import {
  useExportStartAndEndWeekIds,
  useForecastComparisonStartAndEndWeekIds,
  useForecastType,
  useLatestForecastModel
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/hooks';
import { calculatePercentChange, getAppStage } from 'src/utils/app';
import { useBeaconTrafficDataByWeekId } from './serverProxy/useBeaconTrafficDataByWeekId';
import { useAppSelector, useQueryParamValue } from 'src/utils/Hooks';
import { ForecastPeriod, ForecastType } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/types';
import { useYtdStartAndEndWeekIdsForForecast, usePreviousForecastWeekId } from './serverProxy/hooks';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useRetailerDisplayName } from 'src/utils/Hooks/reduxSelectors';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { ExportConfig } from 'src/components/BeaconV2Components/common/AppExportModal/exportDataUtils';

/**
 * Type for a single row in the table
 */
interface SalesTrafficDatum {
  weekId: string;
  retailSales_sum_value: number;
  retailSalesPercentChange: number;
  unitsSold_sum_value: number;
  unitsSoldPercentChange: number;
  totalTraffic_sum_value: number;
  conversionRate: number;
  conversionRatePercentChange: number;
}

/**
 * Helper type to return each row in the table without the percent change values.
 * We do this so that we can combine forcasted and actual data in one array,
 * then compute the percent change for each row in a single computation.
 */
type SalesTrafficDatumWithoutPercentChanges = Omit<
  SalesTrafficDatum,
  'retailSalesPercentChange' | 'unitsSoldPercentChange' | 'conversionRatePercentChange' | 'conversionRate'
>;

/**
 * Formats a week ID like Aug 19, 2023
 */
function getLastDayOfWeek(weekId: number) {
  const year = Math.floor(weekId / 100);
  const week = weekId % 100;

  const firstDayOfWeek = moment().year(year).week(week).day(0);
  const lastDayOfWeek = firstDayOfWeek.clone().day(6);

  return lastDayOfWeek.format('MMM D, YYYY');
}

/**
 * Shows a list of the forecasted sales data for a product
 */
const ForecastsProductSalesByWeekTable = () => {
  const appStage = getAppStage();
  const {
    type: mainEntityType,
    shortDisplayName,
    name: clientName
  } = useAppSelector((state) => state.entityService.mainEntity);
  const retailerDisplayName = useRetailerDisplayName();

  /* ************************** Helper variables - week IDs and forecast period, forecast type ************************** */
  const { historicStartWeekId, historicEndWeekId, forecastEndWeekId, forecastStartWeekId } =
    useExportStartAndEndWeekIds();
  const forecastType = useForecastType();
  const { startWeekId: comparisonStartWeekId, endWeekId: comparisonEndWeekId } =
    useForecastComparisonStartAndEndWeekIds();
  const { startWeekId: mainStartWeekId, endWeekId: mainEndWeekId } = useYtdStartAndEndWeekIdsForForecast();
  const previousForecastWeekId = usePreviousForecastWeekId();
  const forecastPeriod = useQueryParamValue('forecastPeriod', ForecastPeriod.FULL_YEAR);

  /* ******************** Fetched data - forecasts, sales, traffic, and comparisons ********************* */
  const { data: forecastedData, isLoading: isForecastLoading } = useKeyMetricForecastSummaryData();

  const { data: salesData, isLoading: isSalesLoading } = useBeaconSalesDataByWeekId({
    requestId: 'forecastProductTableSales',
    startWeekId: mainStartWeekId,
    endWeekId: mainEndWeekId
  });

  const { data: trafficData, isLoading: isTrafficLoading } = useBeaconTrafficDataByWeekId({
    requestId: 'forecastProductTableTraffic',
    startWeekId: mainStartWeekId,
    endWeekId: mainEndWeekId
  });

  const { data: comparisonData, isLoading: isComparisonLoading } = useBeaconSalesDataByWeekId({
    requestId: 'forecastProductTableSalesComparison',
    startWeekId: comparisonStartWeekId,
    endWeekId: comparisonEndWeekId
  });

  const { data: trafficComparisonData, isLoading: isTrafficComparisonLoading } = useBeaconTrafficDataByWeekId({
    requestId: 'forecastProductTableTrafficComparison',
    startWeekId: comparisonStartWeekId,
    endWeekId: comparisonEndWeekId
  });

  /* ********************  Helper functions for getting sales and traffic ******************************* */

  /** Maps a week ID to sales comparison values */
  const comparisonWeekIdToDatumMap = useMemo(() => {
    return comparisonData.reduce(
      (acc, datum) => ({ ...acc, [datum.weekId]: datum }),
      {} as Record<string, RetailSalesResponseAdditionalValues>
    );
  }, [comparisonData]);

  /** Maps a week ID to traffic comparison values */
  const trafficComparisonWeekIdToDatumMap = useMemo(() => {
    return trafficComparisonData.reduce(
      (acc, datum) => ({ ...acc, [datum.weekId]: datum }),
      {} as Record<string, TrafficResponseAdditionalValues>
    );
  }, [trafficComparisonData]);

  /** Maps a week ID to traffic values */
  const trafficWeekIdToDatumMap = useMemo(() => {
    return trafficData.reduce((acc, datum) => ({ ...acc, [datum.weekId]: datum }), {} as Record<string, TrafficDatum>);
  }, [trafficData]);

  /**
   * Get a sales comparison row for a week id
   */
  const getSalesComparisonDatum = useCallback(
    (weekId: string) => {
      return (
        comparisonWeekIdToDatumMap[previousForecastWeekId(Number(weekId))] || {
          retailSales_sum_value: 0,
          unitsSold_sum_value: 0
        }
      );
    },
    [comparisonWeekIdToDatumMap, previousForecastWeekId]
  );

  /**
   * Get a traffic comparison row for a week id
   */
  const getTrafficComparisonDatum = useCallback(
    (weekId: string) => {
      return (
        trafficComparisonWeekIdToDatumMap[previousForecastWeekId(Number(weekId))] || {
          totalClicks_sum_value: 0
        }
      );
    },
    [previousForecastWeekId, trafficComparisonWeekIdToDatumMap]
  );

  /**
   * Get a traffic row for a week id
   */
  const getTrafficDatum = useCallback(
    (weekId: string) => {
      return trafficWeekIdToDatumMap[weekId] || { totalClicks_sum_value: 0 };
    },
    [trafficWeekIdToDatumMap]
  );

  /* ***************** Calculated data - loading state and formatted rows for table ******************* */
  const isLoading = useMemo(
    () => isForecastLoading || isSalesLoading || isTrafficLoading || isComparisonLoading || isTrafficComparisonLoading,
    [isComparisonLoading, isForecastLoading, isSalesLoading, isTrafficComparisonLoading, isTrafficLoading]
  );

  /** Calculate each metric for the forecasted data and actual sales
   * data (if we're on the full year view)
   */
  const sortedData: SalesTrafficDatum[] = useMemo(() => {
    const combinedData: SalesTrafficDatum[] = [
      // Add in the forecasted data
      ...forecastedData,

      // If we're on the full year, add in the actual sales data
      ...(forecastPeriod === ForecastPeriod.FULL_YEAR
        ? salesData.map((row): SalesTrafficDatumWithoutPercentChanges => {
            return {
              ...row,
              totalTraffic_sum_value: getTrafficDatum(row.weekId).totalClicks_sum_value
            };
          })
        : [])
    ]
      .map((row: SalesTrafficDatumWithoutPercentChanges): SalesTrafficDatum => {
        const conversionRate =
          row.totalTraffic_sum_value > 0 ? row.unitsSold_sum_value / row.totalTraffic_sum_value : 0;
        const comparisonTrafficRow = getTrafficComparisonDatum(row.weekId);
        const comparisonSalesRow = getSalesComparisonDatum(row.weekId);

        const comparisonConversionRate =
          comparisonTrafficRow.totalClicks_sum_value > 0
            ? comparisonSalesRow.unitsSold_sum_value / comparisonTrafficRow.totalClicks_sum_value
            : 0;

        return {
          ...row,
          retailSalesPercentChange: calculatePercentChange(
            row.retailSales_sum_value,
            getSalesComparisonDatum(row.weekId).retailSales_sum_value
          ),
          unitsSoldPercentChange: calculatePercentChange(
            row.unitsSold_sum_value,
            getSalesComparisonDatum(row.weekId).unitsSold_sum_value
          ),
          conversionRate,
          conversionRatePercentChange: calculatePercentChange(conversionRate, comparisonConversionRate)
        };
      })
      .sort((a, b) => a.weekId.localeCompare(b.weekId));

    return combinedData;
  }, [forecastPeriod, forecastedData, getSalesComparisonDatum, getTrafficComparisonDatum, getTrafficDatum, salesData]);

  const retailerId = useAppSelector((state) => state.retailer.id);
  const { currentPublishVersion, modelVersion } = useLatestForecastModel();
  const removeRetailPriceUnadjusted = useRemoveRetailPriceRangeFilters('unadjusted-forecast');
  const removeRetailPriceAdjusted = useRemoveRetailPriceRangeFilters('adjusted-forecast');
  const applyEntityFilters = useApplyEntityFilters();

  const buildUnadjustedForecastExportRequest = ({
    groupByFieldName,
    includeCompTimePeriodForExport
  }: {
    groupByFieldName: string;
    includeCompTimePeriodForExport: boolean;
  }) => {
    const convertedForecastGroupBy = groupByFieldName.replace('weekId', 'forecastWeekId');
    const filename = `${mainEntityType}_${clientName}_forecasts_${forecastType}_${retailerDisplayName}_${
      includeCompTimePeriodForExport ? comparisonStartWeekId : historicStartWeekId
    }_${forecastEndWeekId}`;

    const aggregations = [
      {
        aggregateByFieldDisplayName: 'Total Traffic',
        aggregateByFieldName: 'totalTraffic',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Organic Traffic',
        aggregateByFieldName: 'organicTraffic',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Other Traffic',
        aggregateByFieldName: 'otherTraffic',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Paid Traffic',
        aggregateByFieldName: 'paidTrafficValue',
        func: 'sum',
        canBeExported: true
      },
      { aggregateByFieldDisplayName: 'Ad Spend', aggregateByFieldName: 'adSpend', func: 'sum', canBeExported: true },
      { aggregateByFieldDisplayName: 'Ad Sales', aggregateByFieldName: 'adSales', func: 'sum', canBeExported: true },
      {
        aggregateByFieldDisplayName: 'Weighted Content Score By Content Count',
        aggregateByFieldName: 'weightedContentScoreByContentScoreCount',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Content Score by Units',
        aggregateByFieldName: 'weightedContentScoreByUnits',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Units Sold For Content Score',
        aggregateByFieldName: 'unitsSoldForContentScore',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Content Score Count',
        aggregateByFieldName: 'contentScoreCount',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Star Rating By Number of Reviews',
        aggregateByFieldName: 'weightedRatingByRatingCount',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Buy Box Wins By Units',
        aggregateByFieldName: 'weightedBuyBoxWinRateByUnits',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted In-Stock Rate By Units',
        aggregateByFieldName: 'weightedInStockRateByUnits',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Number of Cumulative Reviews',
        aggregateByFieldName: 'ratingCount',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Number of New Reviews',
        aggregateByFieldName: 'incrementalReviewsFinal',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Retail Sales',
        aggregateByFieldName: 'retailSales',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Units Sold',
        aggregateByFieldName: 'unitsSold',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Wholesale Sales',
        aggregateByFieldName: 'wholesaleSales',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Retailer Margin',
        aggregateByFieldName: 'retailerMargin',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Brand Margin',
        aggregateByFieldName: 'brandMargin',
        func: 'sum',
        canBeExported: true
      }
    ];
    const unAdjustedRequestBuilder = new AdvancedSearchRequestBuilder(
      'unadjustedForecastByweekId_weekId-client',
      'beacon-unadjusted-forecast'
    );

    const unadjustedAggregationBuilder = new AggregationBuilder(convertedForecastGroupBy);

    aggregations.forEach(({ aggregateByFieldDisplayName, aggregateByFieldName, func, canBeExported }) => {
      unadjustedAggregationBuilder.addAggregationField(
        aggregateByFieldDisplayName,
        aggregateByFieldName,
        func,
        canBeExported
      );
    });

    unadjustedAggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);
    unadjustedAggregationBuilder.addConditionTermFilterWithCondition('publishVersion', 'must', [currentPublishVersion]);
    unadjustedAggregationBuilder.addConditionRangeFilter('forecastWeekId', forecastStartWeekId, forecastEndWeekId);
    unadjustedAggregationBuilder.setSortByAggregationField('Retail Sales', 'retailSales', 'sum', true);
    unadjustedAggregationBuilder.setSortDirection('desc');

    // If ForecastType is Adjusted, we should not include this into the request
    if (forecastType === ForecastType.UNADJUSTED) {
      unAdjustedRequestBuilder.setCustomAdditionalMetadata({
        toCompare: includeCompTimePeriodForExport,
        forecastSelection: forecastPeriod === ForecastPeriod.FULL_YEAR ? 'Full Year' : forecastPeriod
      });
    }

    unAdjustedRequestBuilder
      .setPageNumber(1)
      .setPageSize(1200)
      .addConditionRangeFilter('forecastWeekId', forecastStartWeekId, forecastEndWeekId)
      .addConditionTermFilter('retailerId', 'should', [retailerId])
      .addConditionTermFilter('forecastModelVersion', 'must', [modelVersion])
      .addConditionTermFilter('publishVersion', 'must', [currentPublishVersion])
      .addSortField('Retail Sales', 'retailSales', 'sum', true)
      .setPeriod('year')
      .setRetailerId(retailerId)
      .setDoAggregation(true)
      .setReturnDocuments(false)
      .setFileName(filename)
      .setSearchBy('parent')
      .apply(applyEntityFilters)
      .apply(removeRetailPriceUnadjusted)
      .addAggregation(unadjustedAggregationBuilder.build());

    const unadjustedForecastsRequest = unAdjustedRequestBuilder.build();
    return unadjustedForecastsRequest;
  };

  const buildAdjustedForecastExportRequest = ({
    groupByFieldName,
    includeCompTimePeriodForExport
  }: {
    groupByFieldName: string;
    includeCompTimePeriodForExport: boolean;
  }) => {
    const convertedForecastGroupBy = groupByFieldName.replace('weekId', 'forecastWeekId');
    const aggregations = [
      {
        aggregateByFieldDisplayName: 'Total Traffic Delta',
        aggregateByFieldName: 'totalTrafficDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Organic Traffic Delta',
        aggregateByFieldName: 'organicTrafficDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Other Traffic Delta',
        aggregateByFieldName: 'otherTrafficDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Paid Traffic Delta',
        aggregateByFieldName: 'paidTrafficDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Ad Spend Delta',
        aggregateByFieldName: 'adSpendDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Ad Sales Delta',
        aggregateByFieldName: 'adSalesDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Units Sold Delta',
        aggregateByFieldName: 'unitsSoldDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Retail Sales Delta',
        aggregateByFieldName: 'retailSalesDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Content Score By Content Count Delta',
        aggregateByFieldName: 'weightedContentScoreByContentScoreCountDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Content Score by Units Delta',
        aggregateByFieldName: 'weightedContentScoreByUnitsDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Units Sold For Content Score Delta',
        aggregateByFieldName: 'unitsSoldForContentScoreDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Content Score Count Delta',
        aggregateByFieldName: 'contentScoreDelta',
        func: 'avg',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Star Rating By Number of Reviews Delta',
        aggregateByFieldName: 'weightedRatingByRatingCountDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Buy Box Wins By Units Delta',
        aggregateByFieldName: 'weightedBuyBoxWinRateByUnitsDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted In-Stock Rate By Units Delta',
        aggregateByFieldName: 'weightedInStockRateByUnitsDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Number of New Reviews Delta',
        aggregateByFieldName: 'ratingCountDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Wholesale Sales Delta',
        aggregateByFieldName: 'wholesaleSalesDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Retailer Margin Delta',
        aggregateByFieldName: 'retailerMarginDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Brand Margin Delta',
        aggregateByFieldName: 'brandMarginDelta',
        func: 'sum',
        canBeExported: true
      }
    ];
    const adjustedRequestBuilder = new AdvancedSearchRequestBuilder(
      'adjustedForecastByweekId_weekId-client',
      'beacon-adjusted-forecast'
    );

    const adjustedAggregationBuilder = new AggregationBuilder(convertedForecastGroupBy);

    aggregations.forEach(({ aggregateByFieldDisplayName, aggregateByFieldName, func, canBeExported }) => {
      adjustedAggregationBuilder.addAggregationField(
        aggregateByFieldDisplayName,
        aggregateByFieldName,
        func,
        canBeExported
      );
    });

    adjustedAggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);
    adjustedAggregationBuilder.addConditionRangeFilter('forecastWeekId', forecastStartWeekId, forecastEndWeekId);
    adjustedAggregationBuilder.addConditionTermFilterWithCondition('publishVersion', 'must', [currentPublishVersion]);
    adjustedAggregationBuilder.addConditionTermFilterWithCondition('appStage', 'must', [appStage]);
    adjustedAggregationBuilder.setSortByAggregationField('Retail Sales Delta', 'retailSalesDelta', 'sum', true);
    adjustedAggregationBuilder.setSortDirection('desc');

    adjustedRequestBuilder
      .setCustomAdditionalMetadata({
        toCompare: includeCompTimePeriodForExport,
        forecastSelection: forecastPeriod === ForecastPeriod.FULL_YEAR ? 'Full Year' : forecastPeriod
      })
      .setPageNumber(1)
      .setPageSize(1200)
      .addConditionRangeFilter('forecastWeekId', forecastStartWeekId, forecastEndWeekId)
      .addConditionTermFilter('retailerId', 'should', [retailerId])
      .addConditionTermFilter('forecastModelVersion', 'must', [modelVersion])
      .addConditionTermFilter('publishVersion', 'must', [currentPublishVersion])
      .addConditionTermFilter('appStage', 'must', [appStage])
      .addSortField('Retail Sales Delta', 'retailSalesDelta', 'sum', true)
      .setPeriod('year')
      .setRetailerId(retailerId)
      .setDoAggregation(true)
      .setReturnDocuments(false)
      .setSearchBy('parent')
      .apply(applyEntityFilters)
      .apply(removeRetailPriceAdjusted)
      .addAggregation(adjustedAggregationBuilder.build());

    const adjustedForecastsRequest = adjustedRequestBuilder.build();
    return adjustedForecastsRequest;
  };

  return (
    <Flex flexDirection="column">
      <EntityTableHeader
        title="Retail Sales by Week"
        enableSwitchingLayouts
        iconsList={[IconsList.export]}
        defaultView="table"
        buildExportRequest={({ originalRequest, groupByFieldName, includeCompTimePeriod }) => {
          const unadjustedExportRequest = buildUnadjustedForecastExportRequest({
            groupByFieldName,
            includeCompTimePeriodForExport: includeCompTimePeriod
          });
          const adjustedExportRequest = buildAdjustedForecastExportRequest({
            groupByFieldName,
            includeCompTimePeriodForExport: includeCompTimePeriod
          });
          const convertHistoricRequest = originalRequest.map((exportRequest) => {
            // this is a special case only for the ratings and reviews forecasts export
            if (exportRequest.searchType === 'beacon-ratings-reviews-metrics') {
              exportRequest.conditions.rangeFilters = [
                { fieldName: 'weekId', minValue: historicStartWeekId, maxValue: historicEndWeekId }
              ];
            }
            // override the filename to include the forecast week id

            exportRequest.fileName = `${mainEntityType}_${shortDisplayName}_forecasts_${forecastType}_${retailerDisplayName}_${
              includeCompTimePeriod ? comparisonStartWeekId : historicStartWeekId
            }_${forecastEndWeekId}`;
            return {
              ...exportRequest,
              exportResults: true,
              aggregations: [
                {
                  ...exportRequest.aggregations[0],
                  ...(includeCompTimePeriod && forecastPeriod === ForecastPeriod.FULL_YEAR
                    ? {
                        comparisonRangeFilters: [
                          { fieldName: 'weekId', maxValue: comparisonEndWeekId, minValue: comparisonStartWeekId }
                        ]
                      }
                    : {}),
                  conditions: {
                    ...exportRequest.aggregations[0].conditions,
                    rangeFilters: [{ fieldName: 'weekId', minValue: historicStartWeekId, maxValue: historicEndWeekId }]
                  }
                }
              ]
            };
          });

          return [
            ...(includeCompTimePeriod || forecastPeriod === ForecastPeriod.FULL_YEAR ? convertHistoricRequest : []),
            unadjustedExportRequest,
            ...(forecastType === ForecastType.ADJUSTED ? [adjustedExportRequest] : [])
          ] as ExportConfig[];
        }}
      />
      <EntityTable
        isLoading={isLoading || isComparisonLoading || isTrafficComparisonLoading}
        shouldModifyColumnDefs={false}
        columnDefs={[
          {
            headerName: 'Week Ending',
            valueFormatter: (_, row: SalesTrafficDatum) => (
              <Text variant="body2">{getLastDayOfWeek(Number(row.weekId))}</Text>
            )
          },
          {
            headerName: 'Retail Sales',
            valueFormatter: (_, row: SalesTrafficDatum) => (
              <MetricCell
                metricType={METRICTYPE.MONEY}
                value={row.retailSales_sum_value}
                percentChange={row.retailSalesPercentChange}
              />
            )
          },
          {
            headerName: 'Units Sold',
            valueFormatter: (_, row: SalesTrafficDatum) => (
              <MetricCell
                metricType={METRICTYPE.VOLUME}
                value={row.unitsSold_sum_value}
                percentChange={row.unitsSoldPercentChange}
              />
            )
          },
          {
            headerName: 'Total Traffic',
            valueFormatter: (_, row: SalesTrafficDatum) => (
              <MetricCell metricType={METRICTYPE.VOLUME} value={row.totalTraffic_sum_value} />
            )
          },
          {
            headerName: 'Conversion Rate',
            valueFormatter: (_, row: SalesTrafficDatum) => (
              <MetricCell
                metricType={METRICTYPE.PERCENT}
                value={row.conversionRate}
                percentChange={row.conversionRatePercentChange}
              />
            )
          }
        ]}
        rowData={sortedData}
      />
    </Flex>
  );
};

export default ForecastsProductSalesByWeekTable;
